'use client';

import { SystemLoadingSwitch } from '@/components/ui/SystemLoading';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ExternalLink, FileText, Receipt, RefreshCw } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';

import { Modal } from '@/components/ui';
import { StudioAction } from '@/components/ui/StudioAction';
import { CreationGlassBackdrop } from '@/components/visual-editor/CreationGlassBackdrop';
import creationChrome from '@/components/visual-editor/CreationChrome.module.css';
import styles from './OrdersInvoicesModal.module.css';
import { SystemLoadingSpinner } from '@/components/ui/SystemLoading';
import {
  subscriptionsApi,
  type InvoiceRequestSummary,
  type SubmitInvoicePayload,
  type UserOrderSummary,
} from '@/lib/api/subscriptions';
import { toast } from '@/lib/stores/toast-store';
import { cn } from '@/lib/cn';
import { replaceCreditUnitLabel } from '@/lib/credits/unit';
import {
  MERCHANT_ADDRESS,
  MERCHANT_BUSINESS_REGISTRATION_NO,
  MERCHANT_LEGAL_NAME_EN,
  MERCHANT_LEGAL_NAME_ZH,
  SUPPORT_EMAIL,
} from '@/lib/legal/merchant';
import { useAuthStore } from '@/stores/authStore';
import { withAsyncTimeout } from '@/lib/async-timeout';

const BILLING_HISTORY_TIMEOUT_MS = 12_000;

interface OrdersInvoicesModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultEmail: string;
}

type BillingTab = 'orders' | 'invoices';

const ORDER_STATUS_STYLES: Record<string, string> = {
  paid: 'bg-emerald-50 text-emerald-700',
  processing: 'bg-blue-50 text-blue-700',
  pending: 'bg-amber-50 text-amber-700',
  expired: 'bg-gray-100 text-gray-600',
  amount_mismatch: 'bg-red-50 text-red-600',
  limit_rejected: 'bg-red-50 text-red-600',
  resolved: 'bg-blue-50 text-blue-700',
  refunded: 'bg-emerald-50 text-emerald-700',
  ignored: 'bg-gray-100 text-gray-600',
};

const INVOICE_STATUS_STYLES: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700',
  processing: 'bg-blue-50 text-blue-700',
  issued: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-600',
};

function formatDateTime(value: string | null | undefined, locale: string) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString(locale);
}

function formatCurrency(amount: number, currency: string | null | undefined, locale: string) {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function getSafeExternalDocumentUrl(value?: string | null) {
  if (!value) return null;
  try {
    const parsed = new URL(value, window.location.origin);
    const isLocalDevelopmentUrl = process.env.NODE_ENV === 'development'
      && parsed.protocol === 'http:'
      && (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1');
    return parsed.protocol === 'https:' || isLocalDevelopmentUrl ? parsed.href : null;
  } catch {
    return null;
  }
}

export function OrdersInvoicesModal({ isOpen, onClose, defaultEmail }: OrdersInvoicesModalProps) {
  const t = useTranslations('billing');
  const pricingT = useTranslations('pricing');
  const commonT = useTranslations('common');
  const locale = useLocale();
  const id = useId();
  const authenticatedUserId = useAuthStore((state) => state.user?.id ?? null);
  const [activeTab, setActiveTab] = useState<BillingTab>('orders');
  const [orders, setOrders] = useState<UserOrderSummary[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRequestSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<UserOrderSummary | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<{ field: 'title' | 'email' | 'tax' | null; message: string } | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const taxInputRef = useRef<HTMLInputElement>(null);
  const fetchRequestVersionRef = useRef(0);
  const submitInFlightRef = useRef(false);
  const [formState, setFormState] = useState<SubmitInvoicePayload>({
    invoice_title_type: 'personal',
    invoice_type: 'ordinary',
    invoice_title: '',
    tax_number: '',
    email: defaultEmail,
    remark: '',
  });

  useEffect(() => {
    if (!isOpen) { setFormOpen(false); setFormError(null); return; }
    setFormState((prev) => ({ ...prev, email: prev.email || defaultEmail }));
  }, [defaultEmail, isOpen]);

  useEffect(() => {
    fetchRequestVersionRef.current += 1;
    setOrders([]);
    setInvoices([]);
    setSelectedOrder(null);
    setFormOpen(false);
    setFormError(null);
  }, [authenticatedUserId]);

  const fetchData = useCallback(async (showSpinner = true) => {
    const requestVersion = ++fetchRequestVersionRef.current;
    const requestUserId = authenticatedUserId;
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setLoadError(null);
    try {
      const [ordersRes, invoicesRes] = await withAsyncTimeout(
        Promise.all([
          subscriptionsApi.listOrders({ limit: 50 }),
          subscriptionsApi.listInvoices({ limit: 50 }),
        ]),
        BILLING_HISTORY_TIMEOUT_MS,
        'Billing history lookup timed out',
      );
      if (!ordersRes.success || ordersRes.error || !ordersRes.data || ordersRes.data.success === false) throw ordersRes.error ?? new Error(t('loadFailed'));
      if (!invoicesRes.success || invoicesRes.error || !invoicesRes.data || invoicesRes.data.success === false) throw invoicesRes.error ?? new Error(t('loadFailed'));
      if (
        requestVersion !== fetchRequestVersionRef.current
        || (useAuthStore.getState().user?.id ?? null) !== requestUserId
      ) return;
      setOrders(ordersRes.data?.items || []);
      setInvoices(invoicesRes.data?.items || []);
    } catch (error) {
      if (
        requestVersion !== fetchRequestVersionRef.current
        || (useAuthStore.getState().user?.id ?? null) !== requestUserId
      ) return;
      setLoadError(t('loadFailed'));
      toast.showError(error, 'payment', { fallbackMessage: t('loadFailed') });
    } finally {
      if (
        requestVersion !== fetchRequestVersionRef.current
        || (useAuthStore.getState().user?.id ?? null) !== requestUserId
      ) return;
      setLoading(false);
      setRefreshing(false);
    }
  }, [authenticatedUserId, t]);

  useEffect(() => {
    if (!isOpen) return;
    void fetchData();
    return () => {
      fetchRequestVersionRef.current += 1;
    };
  }, [fetchData, isOpen]);

  const getLocalizedOrderTitle = useCallback((order: UserOrderSummary) => {
    if (order.order_type === 'topup') {
      return `${order.total_credits.toLocaleString(locale)} ${replaceCreditUnitLabel(pricingT('credits'), order, locale)}`;
    }
    const planName = order.plan_slug
      ? order.plan_slug[0].toUpperCase() + order.plan_slug.slice(1)
      : '';
    return `${planName} ${pricingT(`paymentOrder.orderTypeLabels.${order.order_type}`)}`.trim();
  }, [locale, pricingT]);

  const openInvoiceForm = (order: UserOrderSummary) => {
    setFormError(null);
    const existing = order.invoice_request;
    setSelectedOrder(order);
    setFormState({
      invoice_title_type: existing?.invoice_title_type || 'personal',
      invoice_type: 'ordinary',
      invoice_title: existing?.invoice_title || '',
      tax_number: existing?.tax_number || '',
      email: existing?.email || defaultEmail,
      remark: existing?.remark || '',
    });
    setFormOpen(true);
  };

  const handleSubmitInvoice = async () => {
    if (!selectedOrder || submitInFlightRef.current) return;
    if (!formState.invoice_title.trim()) {
      setFormError({ field: 'title', message: t('invoiceTitleRequired') });
      titleInputRef.current?.focus();
      toast.error(t('invoiceTitleRequired'));
      return;
    }
    if (!formState.email.trim()) {
      setFormError({ field: 'email', message: t('emailRequired') });
      emailInputRef.current?.focus();
      toast.error(t('emailRequired'));
      return;
    }
    if (formState.invoice_title_type === 'company' && !formState.tax_number?.trim()) {
      setFormError({ field: 'tax', message: t('taxNumberRequired') });
      taxInputRef.current?.focus();
      toast.error(t('taxNumberRequired'));
      return;
    }

    submitInFlightRef.current = true;
    setFormError(null);
    const requestUserId = authenticatedUserId;
    setSubmitting(true);
    try {
      const res = await subscriptionsApi.submitInvoice(selectedOrder.out_trade_no, {
        ...formState,
        invoice_title: formState.invoice_title.trim(),
        email: formState.email.trim(),
        tax_number: formState.tax_number?.trim(),
        remark: formState.remark?.trim(),
      });
      if (!res.success || res.error || res.data?.success === false) throw res.error ?? new Error(t('submitFailed'));
      if ((useAuthStore.getState().user?.id ?? null) !== requestUserId) return;
      toast.success(t('submitSuccess'));
      setFormOpen(false);
      setActiveTab('invoices');
      await fetchData(false);
    } catch (error) {
      if ((useAuthStore.getState().user?.id ?? null) !== requestUserId) return;
      setFormError({ field: null, message: t('submitFailed') });
      toast.showError(error, 'payment', { fallbackMessage: t('submitFailed') });
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const loadingState = useMemo(() => {
    if (!loading && !refreshing) return null;
    return (
      <div role="status" aria-live="polite" className={cn('flex items-center justify-center gap-3 text-sm font-medium text-[#1D7FA6]', loading ? 'py-20' : 'py-4')}>
        <SystemLoadingSpinner size="sm" />
        <span>{commonT('loading')}</span>
      </div>
    );
  }, [commonT, loading, refreshing]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('title')}
        subtitle={t('subtitle')}
        size="xl"
        className={cn(creationChrome.chrome, styles.modal, 'max-w-5xl')}
      >
        <CreationGlassBackdrop />
        <div className="min-w-0 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex max-w-full rounded-xl bg-surface-overlay p-1" role="tablist" aria-label={t('title')}>
              {(['orders', 'invoices'] as BillingTab[]).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  id={`${id}-tab-${tab}`}
                  data-billing-tab={tab}
                  aria-controls={`${id}-panel-${tab}`}
                  aria-selected={activeTab === tab}
                  tabIndex={activeTab === tab ? 0 : -1}
                  onClick={() => setActiveTab(tab)}
                  onKeyDown={(event) => {
                    const next = event.key === 'Home' ? 'orders' : event.key === 'End' ? 'invoices'
                      : ['ArrowLeft', 'ArrowRight'].includes(event.key) ? (tab === 'orders' ? 'invoices' : 'orders') : null;
                    if (!next) return;
                    event.preventDefault();
                    setActiveTab(next);
                    event.currentTarget.parentElement?.querySelector<HTMLButtonElement>(`[data-billing-tab="${next}"]`)?.focus();
                  }}
                  className={cn(
                    'min-h-11 min-w-0 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                    activeTab === tab
                      ? 'bg-white text-hr-text-primary shadow-subtle'
                      : 'text-hr-text-tertiary hover:text-hr-text-secondary',
                  )}
                >
                  {tab === 'orders' ? t('tabs.orders') : t('tabs.invoices')}
                </button>
              ))}
            </div>
            <StudioAction className="gap-2" type="button"
              variant="secondary"
              size="compact"
              onClick={() => void fetchData(false)}
              disabled={loading || refreshing}
              aria-busy={refreshing}
            >
              <SystemLoadingSwitch icon={RefreshCw} className={cn('h-4 w-4', refreshing && 'animate-spin')} />
              {commonT('refresh')}
            </StudioAction>
          </div>

          {loadError ? (
            <div className="flex flex-col items-center justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 sm:flex-row" role="alert">
              <span>{loadError}</span>
              <StudioAction className="gap-2" type="button"
                variant="secondary"
                size="compact"
                onClick={() => void fetchData(false)}
                disabled={loading || refreshing}
              >
                <RefreshCw className="h-4 w-4" />
                {commonT('retry')}
              </StudioAction>
            </div>
          ) : null}

          <div id={`${id}-panel-orders`} role="tabpanel" aria-labelledby={`${id}-tab-orders`} hidden={activeTab !== 'orders'} tabIndex={0} aria-busy={loading || refreshing} className="min-w-0 space-y-3">
            {loadingState}
            {!loading && !refreshing && !loadError && orders.length === 0 && (
              <div className="rounded-2xl border border-hr-border-dim bg-white px-6 py-14 text-center text-sm text-hr-text-tertiary">
                {t('noOrders')}
              </div>
            )}
            {!loading && orders.map((order) => {
              const invoiceStatus = order.invoice_status || 'none';
              const canApplyInvoice = order.status === 'paid' && (!order.invoice_request || order.invoice_status === 'rejected');
              const orderTitle = getLocalizedOrderTitle(order);
              const paymentProvider = order.payment_provider === 'airwallex'
                ? pricingT('paymentMethodAirwallex')
                : order.payment_provider?.startsWith('wechatpay')
                  ? pricingT('paymentMethodWechat')
                  : pricingT('paymentMethodAlipay');
              return (
                <div key={order.out_trade_no} className={`${styles.entry} rounded-2xl border border-hr-border-dim bg-white/70 p-5`}>
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-hr-text-primary">{orderTitle}</h3>
                        <span className={cn('rounded-full px-2 py-1 text-xs font-medium', ORDER_STATUS_STYLES[order.status] || 'bg-gray-100 text-gray-600')}>
                          {t(`orderStatus.${order.status}`)}
                        </span>
                        <span className={cn('rounded-full px-2 py-1 text-xs font-medium', invoiceStatus === 'none' ? 'bg-gray-100 text-gray-600' : INVOICE_STATUS_STYLES[invoiceStatus] || 'bg-gray-100 text-gray-600')}>
                          {invoiceStatus === 'none' ? t('invoiceStatus.none') : t(`invoiceStatus.${invoiceStatus}`)}
                        </span>
                      </div>
                      <div className="mt-3 grid gap-2 text-xs text-hr-text-secondary md:grid-cols-2">
                        <p>{t('fields.orderId')}: {order.out_trade_no}</p>
                        <p>{t('fields.amount')}: {formatCurrency(order.amount_yuan, order.currency, locale)}</p>
                        <p>{t('fields.paymentProvider')}: {paymentProvider}</p>
                        <p>{t('fields.createdAt')}: {formatDateTime(order.created_at, locale)}</p>
                        <p>{t('fields.paidAt')}: {formatDateTime(order.paid_at, locale)}</p>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2 md:justify-end">
                      <StudioAction className="gap-2" type="button"
                        variant="secondary"
                        size="compact"
                        onClick={() => {
                          const currentPath = `${window.location.pathname}${window.location.search}`;
                          window.location.href = `/payment/order/${encodeURIComponent(order.out_trade_no)}?from=${encodeURIComponent(currentPath)}`;
                        }}
                      >
                        <Receipt className="h-4 w-4" />
                        {t('viewOrder')}
                      </StudioAction>
                      {canApplyInvoice && (
                        <StudioAction className="gap-2" type="button"
                          variant="secondary"
                          size="compact"
                          onClick={() => openInvoiceForm(order)}
                        >
                          <FileText className="h-4 w-4" />
                          {order.invoice_status === 'rejected' ? t('resubmitInvoice') : t('applyInvoice')}
                        </StudioAction>
                      )}
                      {getSafeExternalDocumentUrl(order.invoice_request?.invoice_url) && (
                        <StudioAction className="gap-2" type="button"
                          variant="secondary"
                          size="compact"
                          onClick={() => window.open(getSafeExternalDocumentUrl(order.invoice_request?.invoice_url)!, '_blank', 'noopener,noreferrer')}
                        >
                          <ExternalLink className="h-4 w-4" />
                          {t('viewInvoice')}
                        </StudioAction>
                      )}
                    </div>
                  </div>
                  {order.invoice_request?.admin_note && (
                    <div className="mt-4 rounded-xl bg-surface-hover px-3 py-2 text-xs text-hr-text-secondary">
                      {t('fields.adminNote')}: {order.invoice_request.admin_note}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div id={`${id}-panel-invoices`} role="tabpanel" aria-labelledby={`${id}-tab-invoices`} hidden={activeTab !== 'invoices'} tabIndex={0} aria-busy={loading || refreshing} className="min-w-0 space-y-3">
            {loadingState}
            {!loading && !refreshing && !loadError && invoices.length === 0 && (
              <div className="rounded-2xl border border-hr-border-dim bg-white px-6 py-14 text-center text-sm text-hr-text-tertiary">
                {t('noInvoices')}
              </div>
            )}
            {!loading && invoices.map((invoice) => (
              <div key={invoice.id} className={`${styles.entry} rounded-2xl border border-hr-border-dim bg-white/70 p-5`}>
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-hr-text-primary">{invoice.invoice_title}</h3>
                      <span className={cn('rounded-full px-2 py-1 text-xs font-medium', INVOICE_STATUS_STYLES[invoice.status] || 'bg-gray-100 text-gray-600')}>
                        {t(`invoiceStatus.${invoice.status}`)}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-2 text-xs text-hr-text-secondary md:grid-cols-2">
                      <p>{t('fields.orderId')}: {invoice.out_trade_no}</p>
                      <p>{t('fields.amount')}: {formatCurrency(invoice.amount_yuan, invoice.currency, locale)}</p>
                      <p>{t('fields.email')}: {invoice.email}</p>
                      <p>{t('fields.createdAt')}: {formatDateTime(invoice.created_at, locale)}</p>
                    </div>
                    {invoice.invoice_number && (
                      <p className="mt-3 text-xs text-hr-text-secondary">{t('fields.invoiceNumber')}: {invoice.invoice_number}</p>
                    )}
                    {invoice.admin_note && (
                      <div className="mt-3 rounded-xl bg-surface-hover px-3 py-2 text-xs text-hr-text-secondary">
                        {t('fields.adminNote')}: {invoice.admin_note}
                      </div>
                    )}
                  </div>
                  {getSafeExternalDocumentUrl(invoice.invoice_url) && (
                    <StudioAction className="gap-2" type="button"
                      variant="secondary"
                      size="compact"
                      onClick={() => window.open(getSafeExternalDocumentUrl(invoice.invoice_url)!, '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink className="h-4 w-4" />
                      {t('viewInvoice')}
                    </StudioAction>
                  )}
                </div>
              </div>
            ))}
          </div>

          <section className="rounded-2xl border border-hr-border-dim bg-surface-base px-5 py-4 text-xs text-hr-text-secondary">
            <h3 className="text-sm font-semibold text-hr-text-primary">{t('merchant.title')}</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              <p><span className="font-medium text-hr-text-primary">{t('merchant.legalName')}:</span> {locale === 'zh' ? MERCHANT_LEGAL_NAME_ZH : MERCHANT_LEGAL_NAME_EN}</p>
              <p><span className="font-medium text-hr-text-primary">{t('merchant.registration')}:</span> {MERCHANT_BUSINESS_REGISTRATION_NO}</p>
              <p className="md:col-span-2"><span className="font-medium text-hr-text-primary">{t('merchant.address')}:</span> {MERCHANT_ADDRESS}</p>
              <p><span className="font-medium text-hr-text-primary">{t('merchant.support')}:</span> {SUPPORT_EMAIL}</p>
            </div>
          </section>
        </div>
      </Modal>

      <Modal
        isOpen={isOpen && formOpen}
        onClose={() => { if (!submitting) setFormOpen(false); }}
        title={t('invoiceForm.title')}
        subtitle={selectedOrder ? getLocalizedOrderTitle(selectedOrder) : undefined}
        size="lg"
        persistent={submitting}
        className={cn(creationChrome.chrome, styles.modal)}
        footer={<div className="flex flex-wrap justify-end gap-3">
          <StudioAction className="gap-2" type="button" variant="secondary" onClick={() => setFormOpen(false)} disabled={submitting}>
            {commonT('cancel')}
          </StudioAction>
          <StudioAction className="gap-2" type="submit" form={`${id}-form`} aria-busy={submitting} disabled={submitting}>
            {submitting ? <SystemLoadingSpinner size="sm" /> : <Receipt className="h-4 w-4" aria-hidden="true" />}
            {t('invoiceForm.submit')}
          </StudioAction>
        </div>}
      >
        <CreationGlassBackdrop />
        <form id={`${id}-form`} noValidate aria-busy={submitting} onSubmit={(event) => { event.preventDefault(); void handleSubmitInvoice(); }}>
          <fieldset disabled={submitting} className="min-w-0 space-y-5">
            <fieldset className="grid min-w-0 gap-2">
              <legend className="mb-2 text-sm font-medium text-hr-text-secondary">{t('invoiceForm.titleType')}</legend>
              <div className="flex gap-2">
                {(['personal', 'company'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={formState.invoice_title_type === type}
                    onClick={() => { setFormError(null); setFormState((prev) => ({ ...prev, invoice_title_type: type, tax_number: type === 'personal' ? '' : prev.tax_number })); }}
                    className={cn(
                      'min-h-11 min-w-0 flex-1 rounded-xl border px-4 py-3 text-sm transition-colors',
                      formState.invoice_title_type === type
                        ? 'border-accent-core/25 bg-accent-core/10 font-semibold text-accent-core'
                        : 'border-hr-border-dim bg-white text-hr-text-secondary hover:border-hr-border hover:bg-surface-hover',
                    )}
                  >
                    {t(`invoiceForm.titleTypeOptions.${type}`)}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="grid gap-2">
              <label htmlFor={`${id}-title`} className="text-sm font-medium text-hr-text-secondary">{t('invoiceForm.invoiceTitle')}</label>
              <input
                id={`${id}-title`}
                ref={titleInputRef}
                aria-required="true"
                aria-invalid={formError?.field === 'title'}
                aria-describedby={formError?.field === 'title' ? `${id}-error` : undefined}
                value={formState.invoice_title}
                onChange={(event) => { setFormError(null); setFormState((prev) => ({ ...prev, invoice_title: event.target.value })); }}
                placeholder={t('invoiceForm.invoiceTitlePlaceholder')}
                className="h-11 rounded-xl border border-hr-border-dim bg-white px-4 text-sm text-hr-text-primary outline-none transition-colors focus:border-accent-core/40"
              />
            </div>

            {formState.invoice_title_type === 'company' && (
              <div className="grid gap-2">
                <label htmlFor={`${id}-tax`} className="text-sm font-medium text-hr-text-secondary">{t('invoiceForm.taxNumber')}</label>
                <input
                  id={`${id}-tax`}
                  ref={taxInputRef}
                  aria-required="true"
                  aria-invalid={formError?.field === 'tax'}
                  aria-describedby={formError?.field === 'tax' ? `${id}-error` : undefined}
                  value={formState.tax_number}
                  onChange={(event) => { setFormError(null); setFormState((prev) => ({ ...prev, tax_number: event.target.value })); }}
                  placeholder={t('invoiceForm.taxNumberPlaceholder')}
                  className="h-11 rounded-xl border border-hr-border-dim bg-white px-4 text-sm text-hr-text-primary outline-none transition-colors focus:border-accent-core/40"
                />
              </div>
            )}

            <div className="grid gap-2">
              <label htmlFor={`${id}-email`} className="text-sm font-medium text-hr-text-secondary">{t('invoiceForm.email')}</label>
              <input
                id={`${id}-email`}
                ref={emailInputRef}
                aria-required="true"
                aria-invalid={formError?.field === 'email'}
                aria-describedby={formError?.field === 'email' ? `${id}-error` : undefined}
                type="email"
                autoComplete="email"
                value={formState.email}
                onChange={(event) => { setFormError(null); setFormState((prev) => ({ ...prev, email: event.target.value })); }}
                placeholder="name@example.com"
                className="h-11 rounded-xl border border-hr-border-dim bg-white px-4 text-sm text-hr-text-primary outline-none transition-colors focus:border-accent-core/40"
              />
            </div>

            <div className="grid gap-2">
              <label htmlFor={`${id}-remark`} className="text-sm font-medium text-hr-text-secondary">{t('invoiceForm.remark')}</label>
              <textarea
                id={`${id}-remark`}
                value={formState.remark}
                onChange={(event) => setFormState((prev) => ({ ...prev, remark: event.target.value }))}
                placeholder={t('invoiceForm.remarkPlaceholder')}
                rows={3}
                className="rounded-xl border border-hr-border-dim bg-white px-4 py-3 text-sm text-hr-text-primary outline-none transition-colors focus:border-accent-core/40"
              />
            </div>

            <div className="rounded-xl bg-surface-hover px-4 py-3 text-xs text-hr-text-secondary">
              <div className="font-medium text-hr-text-primary">{t('invoiceForm.noticeTitle')}</div>
              <div className="mt-1">{t('invoiceForm.noticeBody')}</div>
            </div>

            {formError && <p id={`${id}-error`} role="alert" className="text-sm text-red-700">{formError.message}</p>}
          </fieldset>
        </form>
      </Modal>
    </>
  );
}
